import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Checkbox, Tooltip, Col, Row } from 'antd';
import { ContractStatus, CompareContractButton, InputRenderer } from '..';
import { ClickableIcon } from '../../../common/components';
import { SearchIcon } from '../../../custom_shared/icons';
import { InputIds, NodeIds } from '../../misc';
import { LayerModel } from '../../models';
import { useAppStore } from '../../stores';
import { useMemoizedInput } from '../../hooks';
import { useNodeColumnWidth } from '../../context/ColumnWidthContext';
import { InputComponentRegistry } from '../../node_inputs';
import './RenewalLayerRenderer.less';

const InputComponents = InputComponentRegistry[NodeIds.GeneralInformation];

const styleConstants = {
    gap: 10,
    checkboxWidth: 36
} as const;

interface Props {
    layer: LayerModel;
}

const RenewalLayerRenderer: React.FC<Props> = ({ layer }) => {
    const businessIdInput = useMemoizedInput(InputIds.GeneralInformation.BusinessId, layer.inputs);
    const expiringPeriodStartInput = useMemoizedInput(InputIds.GeneralInformation.ExpiringPeriodStart, layer.inputs);

    const { getNodeColumnWidth } = useNodeColumnWidth();
    const { businessSearchStore } = useAppStore();

    const columnWidth = (getNodeColumnWidth(NodeIds.GeneralInformation, 8) ?? 300) - styleConstants.gap;

    if (!businessIdInput || !expiringPeriodStartInput) {
        return null;
    }

    return (
        <div className="renewal-layer-container" key={layer.id}>
            <div className="layer-header">
                <span className="layer-label">{layer.name}</span>
            </div>

            <div className="layer-content">
                {layer.layerConfiguration && (
                    <Tooltip title="Select to submit this business" placement="topRight">
                        <div className="layer-submission-checkbox" style={{ width: styleConstants.checkboxWidth }}>
                            <Checkbox
                                checked={!layer.layerConfiguration.skipSubmission}
                                onClick={() => {
                                    const configuration = layer.layerConfiguration;
                                    if (!configuration) {
                                        return;
                                    }
                                    const skipSubmission = !configuration.skipSubmission;
                                    const drafts = businessIdInput.ownerNode.drafts;
                                    drafts.setSkipSubmission(
                                        {
                                            nodeId: businessIdInput.ownerNode.id,
                                            itemId: 'SkipSubmission',
                                            layerId: layer.id
                                        },
                                        skipSubmission
                                    );
                                }}
                            />
                        </div>
                    </Tooltip>
                )}

                <div className="layer-main">
                    <Row className="layer-row">
                        <Col className="layer-col" style={{ width: columnWidth - styleConstants.checkboxWidth }}>
                            <InputRenderer
                                input={businessIdInput}
                                component={InputComponents[InputIds.GeneralInformation.BusinessId]}
                            />
                        </Col>

                        <Col className="layer-col" style={{ width: columnWidth }}>
                            <InputRenderer
                                input={expiringPeriodStartInput}
                                component={InputComponents[InputIds.GeneralInformation.ExpiringPeriodStart]}
                            />
                        </Col>

                        <Col className="layer-col business-search-col">
                            {businessSearchStore.isBusinessSearchSupported && (
                                <Tooltip title="Business Search">
                                    <ClickableIcon
                                        icon={SearchIcon}
                                        width={24}
                                        height={24}
                                        onClick={() => {
                                            businessSearchStore.initializeSearchModal(
                                                businessIdInput,
                                                expiringPeriodStartInput
                                            );
                                        }}
                                    />
                                </Tooltip>
                            )}

                            {!layer.layerConfiguration?.skipSubmission && (
                                <ContractStatus
                                    businessIdInput={businessIdInput}
                                    expiringPeriodStartInput={expiringPeriodStartInput}
                                />
                            )}
                        </Col>
                    </Row>

                    <div className="compare-contract-action-row">
                        <CompareContractButton businessIdInput={businessIdInput} layerId={layer.id} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default observer(RenewalLayerRenderer);
